<script>
import { getDirectoryItem } from "@/entities/directory";

export default {
  name: "ParticipantDropdown",

  data() {
    return {
      visible: false,
      showSecond: false,

      allParticipantTypes: [],
      selectedType: null,
      selectedCodes: [],

      onSave: null,
      onDelete: null,
      onRestore: null,
      onNumberToWords: null,
      hasValue: false,

      position: {
        top: 0,
        left: 0,
      },
    };
  },

  async created() {
    try {
      const data = await getDirectoryItem("participants");
      if (data?.success && data.payload) {
        this.allParticipantTypes = data.payload?.children || [];
      }
    } catch (error) {
      console.error("Ошибка загрузки getDirectoryItem:", error);
    }
  },

  mounted() {
    document.addEventListener("mousedown", this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  },

  computed: {
    participantTypes() {
      if (!this.selectedCodes.length) return [];
      return this.allParticipantTypes.filter((pt) =>
        this.selectedCodes.includes(pt.code)
      );
    },
  },

  methods: {
    openDropdown({
      selectedType,
      selectedCodes,
      onSave,
      onDelete,
      onRestore,
      onNumberToWords,
      hasValue,
      rect,
    }) {
      this.selectedType = selectedType;
      this.selectedCodes = selectedCodes || [];
      this.onSave = onSave;
      this.onDelete = onDelete;
      this.onRestore = onRestore;
      this.onNumberToWords = onNumberToWords;
      this.hasValue = hasValue;

      this.showSecond = false;

      this.$nextTick(() => {
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;
        const dropdownW = 460;
        const dropdownH = 260;

        let top = rect.bottom + window.scrollY + 4;
        let left = rect.left + window.scrollX;

        if (rect.left + dropdownW > viewportW) {
          left = viewportW - dropdownW - 8 + window.scrollX;
        }
        if (rect.bottom + dropdownH > viewportH) {
          top = rect.top + window.scrollY - dropdownH - 4;
        }

        this.position.top = Math.max(4, top);
        this.position.left = Math.max(4, left);
        this.visible = true;
      });
    },

    select(code) {
      this.onSave?.(code);
      this.close();
    },

    remove() {
      this.onDelete?.();
      this.close();
    },

    restore() {
      this.onRestore?.();
      this.close();
    },

    numberToWords() {
      this.onNumberToWords?.();
      this.close();
    },

    handleOutsideClick(e) {
      if (!this.visible) return;
      const dropdown = this.$refs.dropdownRef;
      if (dropdown && !dropdown.contains(e.target)) {
        this.close();
      }
    },

    close() {
      this.visible = false;
      this.showSecond = false;
    },
  },
};
</script>

<template>
  <Transition name="dropdown">
    <div
      v-if="visible"
      ref="dropdownRef"
      class="fixed z-[9999]"
      :style="{
        top: position.top + 'px',
        left: position.left + 'px',
      }"
    >
      <div
        class="flex bg-popover border border-border rounded-lg shadow-md overflow-hidden"
      >
        <!-- Panel 1: Actions -->
        <div class="min-w-[200px] p-1">
          <button
            v-if="selectedType"
            type="button"
            class="menu-item"
            :class="showSecond && 'bg-accent text-accent-foreground'"
            @click="showSecond = true"
          >
            <svg-icon
              name="huge-users"
              class="w-4 h-4 shrink-0 text-muted-foreground"
            />
            <span class="flex-1 text-left">{{ $t("Участник") }}</span>
            <svg-icon
              name="huge-chevron-right"
              class="w-4 h-4 shrink-0 opacity-50"
            />
          </button>

          <button
            v-if="hasValue"
            type="button"
            class="menu-item"
            @click="restore"
          >
            <svg-icon
              name="huge-chevron-left"
              class="w-4 h-4 shrink-0 text-muted-foreground"
            />
            <span class="flex-1 text-left">{{ $t("Вернуть ключ") }}</span>
          </button>

          <button type="button" class="menu-item" @click="numberToWords">
            <svg-icon
              name="huge-text-config"
              class="w-4 h-4 shrink-0 text-muted-foreground"
            />
            <span class="flex-1 text-left">{{ $t("Число прописью") }}</span>
          </button>

          <button
            type="button"
            class="menu-item text-destructive hover:bg-destructive/10"
            @click="remove"
          >
            <svg-icon name="huge-danger" class="w-4 h-4 shrink-0" />
            <span class="flex-1 text-left">{{ $t("Удалить") }}</span>
          </button>
        </div>

        <!-- Panel 2: Participant types -->
        <Transition name="panel">
          <div
            v-if="showSecond"
            class="min-w-[220px] max-h-[300px] overflow-y-auto border-l border-border p-1"
          >
            <div
              v-if="!participantTypes.length"
              class="flex items-center justify-center px-3 py-6 text-sm text-muted-foreground"
            >
              {{ $t("Тип участника не выбран") }}
            </div>
            <button
              v-for="item in participantTypes"
              :key="item.code"
              type="button"
              class="menu-item"
              :class="
                selectedType === item.code
                  ? 'bg-accent text-accent-foreground font-medium'
                  : ''
              "
              @click="select(item.code)"
            >
              <span class="flex-1 text-left">{{
                item.name || item.name_kz
              }}</span>
              <svg-icon
                v-if="selectedType === item.code"
                name="sli-selected"
                class="w-4 h-4 shrink-0 text-primary"
              />
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.menu-item {
  @apply flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer outline-none transition-colors;
  @apply text-foreground hover:bg-accent hover:text-accent-foreground;
}

/* Dropdown appear/disappear */
.dropdown-enter-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}
.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-2px) scale(0.98);
}

/* Second panel slide */
.panel-enter-active {
  transition: opacity 0.15s ease, width 0.15s ease;
}
.panel-leave-active {
  transition: opacity 0.1s ease, width 0.1s ease;
}
.panel-enter-from {
  opacity: 0;
  width: 0;
  min-width: 0;
  overflow: hidden;
}
.panel-leave-to {
  opacity: 0;
  width: 0;
  min-width: 0;
  overflow: hidden;
}
</style>
